/* eslint-disable no-prototype-builtins */
import { useEffect } from 'react';
import { Button, Offcanvas, Spinner, Tab } from 'react-bootstrap';
import {
  crawlerAssetSetting,
  crawlerContent,
  crawlerLimit,
  crawlerSetting,
  crawlerSpeed
} from '../../config/modalSettingsConfig';
import { Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useLazyGetDomainCrawlSettingsQuery,
  useUpdateDomainCrawlSettingsMutation
} from '@/app/Crawl/api/mainCrawlApi';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import CrawlSettingsTab from './CrawlSettingsTab';
import Main from './crawl-settings/Main';
import Assets from './crawl-settings/Assets';
import Limit from './crawl-settings/Limit';
import Content from './crawl-settings/Content';
import UserAgentCralwerSettings from './crawl-settings/UserAgentCralwerSettings';
import { userAgentsCreator } from './crawl-settings/userAgentCreator';
import IncludeExclude from './crawl-settings/IncludeExclude';
import Schedule from './crawl-settings/Schedule';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import useDomain from '@/hooks/useDomain';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';
import type { UserAgentOption } from './crawl-settings/userAgentCreator';
import { errorMessage, successMessage } from '@/utils/toast';
import {
  inferUrlScope,
  type UrlScope
} from '@/app/Crawl/components/common/UrlScopeDropdown';

type CrawlSettingValue =
  | boolean
  | number
  | string
  | string[]
  | UserAgentOption
  | null
  | undefined;

interface CrawlSettingsValues {
  [key: string]: CrawlSettingValue;
  userAgent: UserAgentOption | null;
  exclude_urls_value: string[];
  include_urls_value: string[];
  exclude_urls_is_enable: boolean;
  include_urls_is_enable: boolean;
  custom_user_agent_is_enable: boolean;
  custom_user_agent_value: string;
  custom_extraction_css: string;
  custom_extraction_css_name: string;
  custom_extraction_xpath: string;
  custom_extraction_xpath_name: string;
}

interface CrawlSettingsConfig {
  [key: string]: CrawlSettingValue;
  userAgent?: UserAgentOption | null;
  exclude_urls_value?: string[] | string | null;
  include_urls_value?: string[] | string | null;
}

interface CrawlerSetting {
  name: string;
  defaultValue?: CrawlSettingValue;
}

interface CrawlSettingsDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  config: CrawlSettingsConfig;
  setConfig: (config: CrawlSettingsConfig) => void;
  domain?: string | null;
  startUrl?: string;
  urlScope: UrlScope;
  onUrlScopeChange: (scope: UrlScope) => void;
}

const toStringArray = (
  value: unknown
): string[] => {
  if (Array.isArray(value)) return value.map(String);
  return typeof value === 'string' && value
    ? value.split(',').map(item => item.trim()).filter(Boolean)
    : [];
};

const toBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  return fallback;
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const additionalDefaultSettings: Record<string, CrawlSettingValue> = {
  page_low_internal_outlink: 5,
  query_count_high: 4,
  retry_mode: 1,
  fetcher_mode: 'http',
  force_over_env: false,
  concurrent_requests_per_domain: 1,
  autothrottle_enabled: false,
  autothrottle_start_delay: 2,
  autothrottle_max_delay: 60
};

// تابع مقداردهی تنظیمات در صورت وجود و عدم وجود آنها در api
const initialCreator = (
  config: CrawlSettingsConfig
): CrawlSettingsValues => {
  const initialValues: CrawlSettingsValues = {
    userAgent: null,
    exclude_urls_value: [],
    include_urls_value: [],
    exclude_urls_is_enable: false,
    include_urls_is_enable: false,
    custom_user_agent_is_enable: false,
    custom_user_agent_value: '',
    custom_extraction_css: '',
    custom_extraction_css_name: '',
    custom_extraction_xpath: '',
    custom_extraction_xpath_name: '',
    ...additionalDefaultSettings
  };
  const settings = crawlerSetting as CrawlerSetting[];
  const assetSettings = crawlerAssetSetting as CrawlerSetting[];
  const limitSettings = crawlerLimit as CrawlerSetting[];
  const speedSettings = crawlerSpeed as CrawlerSetting[];
  const contentSettings = crawlerContent as CrawlerSetting[];

  settings.forEach(item => {
    if (config.hasOwnProperty(item.name) && config[item.name] !== null) {
      initialValues[item.name] =
        typeof item.defaultValue === 'boolean'
          ? toBoolean(config[item.name], item.defaultValue)
          : typeof config[item.name] === 'string'
            ? config[item.name]
            : item.defaultValue;
    } else {
      initialValues[item.name] = item.defaultValue;
    }
  });
  initialValues.ignore_query = true;
  assetSettings.forEach(item => {
    if (config.hasOwnProperty(item.name)) {
      initialValues[item.name] = config[item.name];
    } else {
      initialValues[item.name] = item.defaultValue;
    }
  });
  limitSettings.forEach(item => {
    if (config.hasOwnProperty(item.name)) {
      initialValues[item.name] = config[item.name];
    } else {
      initialValues[item.name] = item.defaultValue;
    }
  });
  speedSettings.forEach(item => {
    if (config.hasOwnProperty(item.name)) {
      initialValues[item.name] = config[item.name];
    } else {
      initialValues[item.name] = item.defaultValue;
    }
  });
  contentSettings.forEach(item => {
    if (config.hasOwnProperty(item.name)) {
      initialValues[item.name] = config[item.name];
    } else {
      initialValues[item.name] = item.defaultValue;
    }
  });
  Object.entries(additionalDefaultSettings).forEach(
    ([name, defaultValue]) => {
      initialValues[name] =
        config.hasOwnProperty(name) &&
        config[name] !== null &&
        config[name] !== undefined
          ? config[name]
          : defaultValue;
    }
  );
  // }

  // BUG
  if (config.hasOwnProperty('userAgent')) {
    initialValues['userAgent'] = config['userAgent'];
  } else {
    initialValues['userAgent'] = null;
  }
  initialValues.exclude_urls_value = toStringArray(
    config.exclude_urls_value
  );
  initialValues.include_urls_value = toStringArray(
    config.include_urls_value
  );
  initialValues.exclude_urls_is_enable = toBoolean(
    config.exclude_urls_is_enable,
    false
  );
  initialValues.include_urls_is_enable = toBoolean(
    config.include_urls_is_enable,
    false
  );
  if (!initialValues.exclude_urls_is_enable) {
    initialValues.exclude_urls_value = [];
  }
  if (!initialValues.include_urls_is_enable) {
    initialValues.include_urls_value = [];
  }
  initialValues.custom_user_agent_is_enable = toBoolean(
    config.custom_user_agent_is_enable,
    false
  );
  initialValues.custom_user_agent_value =
    typeof config.custom_user_agent_value === 'string'
      ? config.custom_user_agent_value
      : '';
  initialValues.custom_extraction_css =
    typeof config.custom_extraction_css === 'string'
      ? config.custom_extraction_css
      : '';
  initialValues.custom_extraction_css_name =
    typeof config.custom_extraction_css_name === 'string'
      ? config.custom_extraction_css_name
      : '';
  initialValues.custom_extraction_xpath =
    typeof config.custom_extraction_xpath === 'string'
      ? config.custom_extraction_xpath
      : '';
  initialValues.custom_extraction_xpath_name =
    typeof config.custom_extraction_xpath_name === 'string'
      ? config.custom_extraction_xpath_name
      : '';
  initialValues.url_scope = inferUrlScope(
    config as Record<string, unknown>
  );
  return initialValues;
};

// };

// دراوی تنظیمات خزش
const CrawlSettingsDrawer = ({
  isOpen,
  setIsOpen,
  config,
  setConfig,
  startUrl = '',
  urlScope,
  onUrlScopeChange
}: CrawlSettingsDrawerProps) => {
  const { domain_slug, domain } = useDomain() as {
    domain_slug: string | null;
    domain: string | null;
  };
  useGetUserInfoQuery();
  // دریافت تنظیمات قبلی خزش
  const [
    getSettings,
    { data: crawlSettings, isFetching: isCrawlSettingsLoading }
  ] = useLazyGetDomainCrawlSettingsQuery();
  const [updateSettings, { isLoading: isUpdatingSettings }] =
    useUpdateDomainCrawlSettingsMutation();
  useEffect(() => {
    if (isOpen && domain_slug) {
      void getSettings(domain_slug);
    }
  }, [domain_slug, getSettings, isOpen]);
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  useEffect(() => {
    if (!isOpen || isCrawlSettingsLoading) return;

    const responseData = crawlSettings?.data?.settings;
    if (!responseData) {
      setConfig({});
      return;
    }

    const settings = responseData as CrawlSettingsConfig;
    const customUserAgentValue =
      typeof settings.custom_user_agent_value === 'string'
        ? settings.custom_user_agent_value
        : '';
    const decodedUserAgent = customUserAgentValue
      ? decodeURIComponent(customUserAgentValue)
      : '';
    const customUserAgentEnabled = toBoolean(
      settings.custom_user_agent_is_enable,
      false
    );
    const customOption = userAgentsCreator()[0];
    const lastUserAgent = customUserAgentEnabled
      ? { ...customOption, value: decodedUserAgent }
      : userAgentsCreator().find(
          (agent: UserAgentOption) => agent.value === decodedUserAgent
        );

    setConfig({
      ...settings,
      exclude_urls_value: toStringArray(settings.exclude_urls_value),
      include_urls_value: toStringArray(settings.include_urls_value),
      custom_user_agent_value: decodedUserAgent,
      userAgent: lastUserAgent ?? {
        name: '',
        label: '',
        value: decodedUserAgent
      }
    });
  }, [crawlSettings, isCrawlSettingsLoading, isOpen, setConfig]);

  return (
    <Offcanvas
      show={isOpen}
      onHide={() => {
        setIsOpen(false);
      }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>تنظیمات خزش {domain}</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="crawl-settings-drawer-body">
        <Tab.Container
          defaultActiveKey="crawlerSetting"
          className="crawl-settings-drawer-tabs d-flex flex-column flex-grow-1 min-h-0"
        >
          {/* تب های تنظیمات خزش */}
          <CrawlSettingsTab />
                {!isCrawlSettingsLoading ? (
                  <Formik<CrawlSettingsValues>
                    onSubmit={async (values: CrawlSettingsValues) => {
                      const updatedConfig = {
                        ...values
                      };
                      Reflect.deleteProperty(updatedConfig, 'userAgent');

                      const sent: CrawlSettingsConfig = {
                        ...updatedConfig,
                        depth_high: toNumberOrNull(values.depth_high),
                        max_crawl_duration_hourly: toNumberOrNull(
                          values.max_crawl_duration_hourly
                        ),
                        bad_response_time: toNumberOrNull(
                          values.bad_response_time
                        ),
                        redirect_max_times: toNumberOrNull(
                          values.redirect_max_times
                        ),
                        concurrent_requests: toNumberOrNull(
                          values.concurrent_requests
                        ),
                        download_delay: toNumberOrNull(
                          values.download_delay
                        ),
                        page_low_internal_outlink: toNumberOrNull(
                          values.page_low_internal_outlink
                        ),
                        query_count_high: toNumberOrNull(
                          values.query_count_high
                        ),
                        retry_mode: toNumberOrNull(values.retry_mode),
                        fetcher_mode:
                          typeof values.fetcher_mode === 'string'
                            ? values.fetcher_mode
                            : 'http',
                        force_over_env: toBoolean(
                          values.force_over_env,
                          false
                        ),
                        concurrent_requests_per_domain: toNumberOrNull(
                          values.concurrent_requests_per_domain
                        ),
                        autothrottle_enabled: toBoolean(
                          values.autothrottle_enabled,
                          false
                        ),
                        autothrottle_start_delay: toNumberOrNull(
                          values.autothrottle_start_delay
                        ),
                        autothrottle_max_delay: toNumberOrNull(
                          values.autothrottle_max_delay
                        ),
                        ignore_query: true,
                        ignore_fragment: toBoolean(
                          values.ignore_fragment,
                          true
                        ),
                        ignore_rel_ahref_nofollow: toBoolean(
                          values.ignore_rel_ahref_nofollow,
                          true
                        ),
                        respect_pagination: toBoolean(
                          values.respect_pagination,
                          false
                        ),
                        respect_canonical: toBoolean(
                          values.respect_canonical,
                          false
                        ),
                        custom_encoding_is_enable: toBoolean(
                          values.custom_encoding_is_enable,
                          false
                        ),
                        ignore_noindex_page: toBoolean(
                          values.ignore_noindex_page,
                          false
                        ),
                        exclude_urls_value:
                          values['exclude_urls_value'].join(',').length > 0
                            ? values['exclude_urls_value'].join(',')
                            : '',
                        include_urls_value:
                          values['include_urls_value'].join(',').length > 0
                            ? values['include_urls_value'].join(',')
                            : '',
                        custom_user_agent_is_enable:
                          values.custom_user_agent_is_enable,
                        custom_user_agent_value:
                          values.custom_user_agent_is_enable
                            ? values.custom_user_agent_value
                            : '',
                        custom_extraction_css: values.custom_extraction_css,
                        custom_extraction_css_name:
                          values.custom_extraction_css_name,
                        custom_extraction_xpath: values.custom_extraction_xpath,
                        custom_extraction_xpath_name:
                          values.custom_extraction_xpath_name
                      };
                      if (!domain_slug) {
                        errorMessage(
                          'دامنه‌ای برای ذخیره تنظیمات انتخاب نشده است'
                        );
                        return;
                      }

                      try {
                        console.log(
                          'داده‌های ارسالی تنظیمات خزش به API:',
                          sent
                        );
                        await updateSettings({
                          domain_slug,
                          settings: sent as unknown as Record<string, unknown>
                        }).unwrap();
                        setConfig(sent);
                        successMessage('تنظیمات خزش با موفقیت ذخیره شد');
                        setIsOpen(false);
                      } catch {
                        errorMessage('در ذخیره تنظیمات خزش خطایی رخ داد');
                      }
                    }}
                    initialValues={initialCreator(config)}
                    enableReinitialize
                  >
                    {({
                      values,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      setFieldValue,
                      resetForm
                    }) => (
                      <>
                        <Tab.Content
                          className="crawl-settings-drawer__content flex-grow-1"
                        >
                        {/* تنظیمات اصلی خزش */}
                        <Main
                          values={values}
                          setFieldValue={setFieldValue}
                          domain={domain}
                          startUrl={startUrl}
                          urlScope={urlScope}
                          onUrlScopeChange={onUrlScopeChange}
                        />
                        {/* تنظیمات منابع خزش */}
                        <Assets
                          values={values}
                          setFieldValue={setFieldValue}
                        />
                        {/* تنظیمات محدودیت خزش */}
                        <Limit
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          values={values}
                        />
                        {/* تنظیمات محتوای خزش */}
                        <Content
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          values={values}
                        />
                        {/* تنظیمات یوزر ایجنت خزش */}
                        <UserAgentCralwerSettings
                          setFieldValue={setFieldValue}
                          values={values}
                        />
                        {/* تنظیمات شامل و استثناء */}
                        <IncludeExclude
                          setFieldValue={setFieldValue}
                          values={values}
                        />
                        <Schedule />
                        </Tab.Content>
                        {/* دکمه های آخر صفحه */}
                        <div
                          className="crawl-settings-drawer__footer d-flex flex-wrap gap-2 justify-content-end border-top pt-3 pb-1 px-1 mt-2"
                        >
                          <Button
                            className="d-flex gap-2 align-items-center"
                            variant="outline-secondary"
                            onClick={() => setIsOpen(false)}
                          >
                            <FontAwesomeIcon size="lg" icon="times" />
                            لغو
                          </Button>
                          <Button
                            disabled={
                              !isAccessAllowed() || isUpdatingSettings
                            }
                            className="d-flex gap-2 align-items-center"
                            variant="outline-warning"
                            onClick={async () => {
                              if (!domain_slug) {
                                errorMessage(
                                  'دامنه‌ای برای بازنشانی تنظیمات انتخاب نشده است'
                                );
                                return;
                              }

                              try {
                                console.log(
                                  'داده‌های بازنشانی تنظیمات خزش به API:',
                                  { domain_slug, settings: null }
                                );
                                await updateSettings({
                                  domain_slug,
                                  settings: null
                                }).unwrap();
                                setConfig({});
                                resetForm({
                                  values: initialCreator({})
                                });
                                successMessage(
                                  'تنظیمات خزش به مقادیر پیش‌فرض بازنشانی شد'
                                );
                              } catch {
                                errorMessage(
                                  'در بازنشانی تنظیمات خزش خطایی رخ داد'
                                );
                              }
                            }}
                          >
                            <FontAwesomeIcon size="lg" icon="undo-alt" />
                            ریست تنظیمات
                          </Button>
                          <ChildrensTooltip
                            placement="top"
                            html={
                              !isAccessAllowed()
                                ? 'شما دسترسی لازم را ندارید'
                                : ''
                            }
                          >
                            <Button
                              disabled={
                                !isAccessAllowed() || isUpdatingSettings
                              }
                              className="d-flex gap-2 align-items-center border-0"
                              onClick={() => handleSubmit()}
                              variant="falcon-primary"
                            >
                              <FontAwesomeIcon size="lg" icon="save" />
                              ذخیره تنظیمات
                            </Button>
                          </ChildrensTooltip>
                        </div>
                      </>
                    )}
                  </Formik>
                ) : (
                  <div
                    className="crawl-settings-drawer__content flex-grow-1 d-flex mt-3 align-items-center justify-content-center w-100"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <Spinner size="sm" />
                      درحال دریافت تنظیمات
                    </div>
                  </div>
                )}
        </Tab.Container>
      </Offcanvas.Body>
      <style>
        {`
         .offcanvas{
              width:780px !important;
              max-width:100% !important;
              overflow-x: hidden;
              overflow-y: hidden;
            }
          .crawl-settings-drawer-body {
            display: flex;
            flex-direction: column;
            overflow-x: hidden;
            overflow-y: hidden;
            min-width: 0;
          }
          .crawl-settings-drawer-tabs {
            min-height: 0;
            min-width: 0;
            max-width: 100%;
            overflow-x: hidden;
          }
          .crawl-settings-drawer__nav {
            max-width: 100%;
          }
          .crawl-settings-drawer__content {
            padding-bottom: 0.5rem;
            overflow-x: hidden;
            overflow-y: auto;
            min-width: 0;
            max-width: 100%;
          }
          .crawl-settings-drawer__footer {
            flex-shrink: 0;
            max-width: 100%;
            overflow-x: hidden;
            background: var(--falcon-offcanvas-bg, var(--bs-body-bg));
          }
          .crawl-settings-drawer__nav .nav-link.crawl-settings-tab--soon.disabled,
          .crawl-settings-drawer__nav .nav-link.crawl-settings-tab--soon:disabled {
            color: var(--bs-secondary-color) !important;
            border-color: transparent !important;
            opacity: 0.65;
            cursor: not-allowed;
            pointer-events: none;
          }
          .crawl-settings-drawer__nav
            .nav-link.crawl-settings-tab--soon.disabled
            .crawl-settings-tab__soon-badge,
          .crawl-settings-drawer__nav
            .nav-link.crawl-settings-tab--soon:disabled
            .crawl-settings-tab__soon-badge {
            opacity: 1;
          }
          `}
      </style>
    </Offcanvas>
  );
};

export default CrawlSettingsDrawer;
